import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, type ReactNode } from "react";

import { Button } from "./Button";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "viewport";
  showCloseButton?: boolean;
  className?: string;
  closeOnBackdropClick?: boolean;
}

const sizes = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  "2xl": "max-w-6xl",
  viewport: "max-w-[1400px] md:w-[94vw]",
};

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  showCloseButton = true,
  className,
  closeOnBackdropClick = true,
}: ModalProps) {
  const viewportLayout = size === "viewport";
  const handleDialogClose = () => {
    if (!closeOnBackdropClick) {
      return;
    }

    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        data-workspace-overlay
        className="relative z-50"
        onClose={handleDialogClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm" />
        </Transition.Child>

        <div
          className={`fixed inset-0 ${viewportLayout ? "overflow-hidden" : "overflow-y-auto"}`}
        >
          <div
            className={
              viewportLayout
                ? "flex h-[100dvh] max-h-full items-center justify-center p-[max(0.5rem,env(safe-area-inset-top),env(safe-area-inset-right),env(safe-area-inset-bottom),env(safe-area-inset-left))] text-center"
                : "flex min-h-full items-end justify-center p-2 text-center sm:items-center sm:p-4"
            }
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`ui-dialog-surface w-full ${sizes[size]} ${
                  viewportLayout
                    ? "flex min-h-0 max-h-full flex-col overflow-hidden rounded-2xl md:max-h-[min(90dvh,100%)] md:min-h-[min(640px,90dvh,100%)]"
                    : "max-h-[calc(100vh-1rem)] overflow-y-auto rounded-t-2xl rounded-b-none sm:max-h-[90vh] sm:rounded-2xl"
                } bg-white p-4 text-left align-middle shadow-xl transition-all dark:bg-slate-900 sm:p-6 ${
                  className ?? ""
                }`}
              >
                <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
                  <Dialog.Title
                    as="h3"
                    className="min-w-0 flex-1 text-base font-medium leading-6 text-slate-900 dark:text-white sm:text-lg"
                  >
                    {title}
                  </Dialog.Title>
                  {showCloseButton && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onClose}
                      aria-label={`Close ${title}`}
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </Button>
                  )}
                </div>
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
